import * as React from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Languages, Loader2 } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { useToast } from "@/components/ui/use-toast"
import { api } from "@/lib/api"
import { useAuth } from "@/lib/auth"
import { t, useI18n } from "@/lib/i18n"

/**
 * EL IDIOMA DE LA INTERFAZ, ELEGIDO POR LA PERSONA.
 *
 * Punto 12 de Cesar: «traducción para que cada usuario pueda seleccionar
 * idioma», y después lo precisó: «no por entidad».
 *
 * ---------------------------------------------------------------------------
 * DÓNDE VIVE, Y POR QUÉ SE MOVIÓ
 *
 * Cesar: «no encuentro dónde cambiar el idioma, debería de ser un botón arriba
 * o en la barra de la izquierda».
 *
 * Estaba dentro del menú del avatar, al fondo del riel. El argumento era que el
 * idioma no es un filtro: la barra de arriba lleva las coordenadas del DATO
 * —qué cliente, qué sociedad— y el idioma es una propiedad de QUIEN MIRA, así
 * que al lado de la sociedad parecería otra dimensión del dato.
 *
 * El argumento sigue siendo cierto y aun así estaba mal aplicado: **separaba el
 * idioma de la sociedad metiéndolo donde nadie lo encontraba**. Un control que
 * no se encuentra no está bien colocado, esté bien razonado o no.
 *
 * Ahora es un botón propio arriba a la DERECHA, pegado al tema — que es el otro
 * control que tampoco es del dato sino de quien mira. Los dos ejes quedan
 * separados por el sitio, no por el escondite: coordenadas del dato a la
 * izquierda, preferencias de la persona a la derecha.
 *
 * ---------------------------------------------------------------------------
 * «AUTOMÁTICO» ES UNA OPCIÓN DE VERDAD, NO UN SINÓNIMO DE ESPAÑOL
 *
 * Sin idioma elegido, la interfaz sigue al país de la sociedad que se esté
 * mirando — que es lo que el producto hacía hasta hoy y es un buen valor por
 * defecto: quien abre la sociedad de São Paulo ve la pantalla como la verá
 * quien la firme allá. Ofrecerlo explícitamente permite VOLVER a ese
 * comportamiento; sin la opción, el primer clic sería irreversible.
 *
 * ---------------------------------------------------------------------------
 * LO QUE ESTE SELECTOR NO CAMBIA, Y SE DICE AQUÍ MISMO
 *
 * La terminología fiscal, la moneda y el formato de número. El IGV se llama IGV
 * en cualquier idioma, y un balance brasileño se presenta a la brasileña lo lea
 * quien lo lea. Decirlo en el propio menú evita la decepción de elegir inglés y
 * encontrarse «IGV» en pantalla creyendo que algo falló.
 */

const IDIOMAS: Array<{ id: string; etiqueta: string; nota: string }> = [
  { id: "auto", etiqueta: "Automático", nota: "sigue al país de la sociedad" },
  { id: "es", etiqueta: "Español", nota: "" },
  { id: "pt", etiqueta: "Português", nota: "" },
  { id: "en", etiqueta: "English", nota: "" },
]

export function SelectorDeIdioma() {
  const { profile, refreshProfile } = useAuth()
  const { toast } = useToast()
  const qc = useQueryClient()
  const actual = profile?.idioma ?? "auto"

  const elegir = useMutation({
    mutationFn: (id: string) =>
      api.put<{ idioma: string | null; nota: string }>("/auth/me/idioma", {
        idioma: id === "auto" ? null : id,
      }),
    onSuccess: async (r) => {
      // El perfil se recarga porque es DE DONDE sale el idioma: sin esto habría
      // que guardar el mismo dato en dos sitios y mantenerlos de acuerdo.
      await refreshProfile()
      // Y las consultas en curso se invalidan: los textos que llegan del
      // backend —las notas de los endpoints— también vienen en un idioma.
      void qc.invalidateQueries()
      toast({ title: "Idioma cambiado", description: r.nota })
    },
    onError: () => {
      toast({
        variant: "warning",
        title: "No se pudo guardar el idioma",
        description: "Se guarda en tu perfil, así que hace falta conexión.",
      })
    },
  })

  return (
    <>
      <DropdownMenuLabel className="flex items-center gap-1.5 text-2xs font-normal text-muted-foreground">
        {elegir.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
        {t("Idioma de la interfaz")}</DropdownMenuLabel>
      {IDIOMAS.map((i) => (
        <DropdownMenuCheckboxItem
          key={i.id}
          checked={actual === i.id}
          /*
            `onSelect` con `preventDefault` para que el menú NO se cierre: el
            idioma se cambia mirando el resultado, y un menú que se cierra al
            elegir obliga a volver a abrirlo para comprobar qué quedó puesto.
          */
          onSelect={(e) => { e.preventDefault(); elegir.mutate(i.id) }}
          disabled={elegir.isPending}
          className="text-2xs"
        >
          {i.etiqueta}
          {i.nota ? <span className="ml-1 text-muted-foreground">· {i.nota}</span> : null}
        </DropdownMenuCheckboxItem>
      ))}
      <p className="px-2 pb-1 pt-0.5 text-2xs leading-4 text-muted-foreground">
        {t("Los nombres de los impuestos, la moneda y el formato de número siguen a la sociedad: el IGV se llama IGV en cualquier idioma.")}</p>
    </>
  )
}

/**
 * EL BOTÓN DE LA BARRA, que es el punto de entrada de verdad.
 *
 * Muestra el CÓDIGO del idioma en curso —`ES`, `PT`, `EN`— y no un globo
 * terráqueo a secas: un icono sin texto no dice en qué idioma estás, y la
 * pregunta de Cesar («no encuentro dónde cambiar el idioma») empieza por no
 * poder ver cuál está puesto.
 *
 * Con «Automático» muestra el código que resultó, en gris y con un punto: el
 * idioma se está siguiendo, no eligiendo, y eso hay que poder distinguirlo sin
 * abrir el menú.
 */
export function BotonDeIdioma() {
  const { profile } = useAuth()
  const i18n = useI18n()
  const elegido = profile?.idioma ?? null
  const codigo = (elegido ?? i18n.idioma).toUpperCase()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2"
          title={
            elegido
              ? `Idioma de la interfaz: ${codigo}. Los impuestos, la moneda y el formato siguen a la sociedad.`
              : `Idioma automático: sigue al país de la sociedad (ahora ${codigo}).`
          }
          aria-label={t("Idioma de la interfaz")}
          /*
            EL ANCLA DE LAS PRUEBAS NO PUEDE SER EL `aria-label`, por la misma
            razon que en el selector de sociedad: se traduce, y debe traducirse.
            Y aqui es peor que en ningun sitio — este boton es el que CAMBIA el
            idioma, asi que usarlo renombra su propio nombre accesible y la
            prueba pierde el elemento a mitad de camino.
          */
          data-testid="boton-idioma"
        >
          <Languages className="h-3.5 w-3.5" />
          <span className="code text-2xs font-medium">{codigo}</span>
          {/* El punto dice «esto lo estoy siguiendo yo, no lo elegiste tú». */}
          {elegido ? null : (
            <span
              aria-hidden="true"
              className="h-1 w-1 rounded-full bg-border-strong"
              title={t("Automático")}
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <SelectorDeIdioma />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
